import { UNION, UNIONS } from './src/constants';
import { toStringFilter, toStringV2 } from './src/toStringFilter';
import { toHumanized, toHumanizedCriteria, toHumanizedV2 } from './src/toHumanized';
import cloneFilter from './src/cloneFilter';

import { mapFilterEdiwinToFilterModel } from './mapFilterEdiwinToFilterModel';
import { mapFilterModelToFilterEdiwin } from './mapFilterModelToFilterEdiwin';
import { mapFilterModelToCriterias } from './mapFilterModelToCriterias';

/**
 * class Filter
 *
 */

export class Filter {
  static mapFilterModelToFilterEdiwin(...args) {
    return mapFilterModelToFilterEdiwin.call(args);
  }

  static mapFilterModelToFilterEdiwin(...args) {
    return mapFilterModelToFilterEdiwin.call(args);
  }

  static mapFilterModelToCriterias(...args) {
    return mapFilterModelToCriterias.call(args);
  }

  constructor({
    filterModel = null,
    filter = null,
    filterCriteria = null,
    from = null,
    to = null,
    orderBy = null,
    groupBy = null,
  } = {}) {
    this._cache = {};

    this._filter = filter || {
      filterCriteria: {
        children: [],
        criteria: null,
        union: null,
      },
      // from,
      // to,
      orderBy,
      groupBy,
    };

    if (filterCriteria) {
      this._filter.filterCriteria = filterCriteria;
    }

    if (from) {
      this._filter.from = from;
    }

    if (to) {
      this._filter.to = to;
    }

    if (orderBy) {
      this._filter.orderBy = orderBy;
    }

    if (groupBy) {
      this._filter.groupBy = groupBy;
    }

    if (filterModel) {
      this.filterModel = filterModel;
    }
  }

  /*
   * @method (private)
   * Reinicializa la cache
   */
  _invalidateCache() {
    this._cache = {};
  }

  /*
   * @method
   * Devuelve el filtro en formato string
   */
  toString() {
    return toStringFilter(this._filter);
  }

  /*
   * @method
   * Devuelve el filtro en formato string humanizado (y traducido si se le pasa las traducciones)
   */
  toHumanized(options = {}) {
    return toHumanized(this._filter, options);
  }

  /*
   * @property
   * Es un clone del objeto filtro en formato de Ediwin (ademas limpiado de condiciones IN vacias,...)
   */

  get clone() {
    return cloneFilter(this._filter);
  }

  /*
   * @property
   * Es el objeto filtro en formato de Ediwin
   */
  get filter() {
    return this._filter;
  }

  set filter(filter) {
    this._invalidateCache();
    this._filter = filter;
  }

  /*
   * @property
   * Es el objecto filtro en formato del objeto usado en el ec-ag-grid
   */
  get filterModel() {
    if (this._cache.filterModel) {
      return this._cache.filterModel;
    }
    this._cache.filterModel = mapFilterEdiwinToFilterModel(this._filter);
    return this._cache.filterModel;
  }

  set filterModel(filterModel) {
    this._invalidateCache();
    this._cache.filterModel = filterModel;
    this._filter = mapFilterModelToFilterEdiwin(filterModel, this._filter.orderBy, this._filter.groupBy);
  }

  /*
   * @method
   * Permite definir el from
   *
   * @return this para poder encadenar llamadas
   */
  from(string) {
    this._filter.from = string;
    return this;
  }

  /*
   * @method
   * Permite definir el to
   *
   * @return this para poder encadenar llamadas
   */
  to(string) {
    this._filter.to = string;
    return this;
  }

  /*
   * @method
   * Permite definir el orderBy
   *
   * @return this para poder encadenar llamadas
   */
  orderBy(string) {
    this._filter.orderBy = string;
    return this;
  }

  /*
   * @method
   * Permite definir el groupBy
   *
   * @return this para poder encadenar llamadas
   */
  groupBy(string) {
    this._filter.groupBy = string;
    return this;
  }

  /*
   * @method
   * Permite definir una condición (una unica!!!!)
   *
   * @return this para poder encadenar llamadas
   */
  where(params) {
    // params: {v1, op, v2} o {field, type, value}
    const v1 = typeof params.v1 !== 'undefined' ? params.v1 : params.field;
    const op = typeof params.op !== 'undefined' ? params.op : params.type;
    const v2 = typeof params.v2 !== 'undefined' ? params.v2 : params.value;
    this._invalidateCache();
    this._filter.filterCriteria.criteria = { v1, op, v2 };
    return this;
  }

  /*
   * @method
   * Permite definir multiples AND condiciónes cada una en su propio Filter
   *
   * @return this para poder encadenar llamadas
   */
  and(...filters) {
    // si se pasa un array
    if (filters && filters[0] && Array.isArray(filters[0])) {
      return this._merge(UNION.AND, filters[0]);
    }
    // sino si es una lista de argumentos
    return this._merge(UNION.AND, filters);
  }

  /*
   * @method
   * Permite definir multiples OR condiciónes cada una en su propio Filter
   *
   * @return this para poder encadenar llamadas
   */
  or(...filters) {
    // si se pasa un array
    if (filters && filters[0] && Array.isArray(filters[0])) {
      return this._merge(UNION.OR, filters[0]);
    }
    // sino si es una lista de argumentos
    return this._merge(UNION.OR, filters);
  }

  /*
   * @method (private)
   * Permite definir multiples condiciónes cada una en su propio Filter
   * @param {string} union - tipo de union "AND" o "OR"
   * @param {[Filter]} filters - Array de [Filter]
   *
   * @return this para poder encadenar llamadas
   */
  _merge(union, filters) {
    this._invalidateCache();
    this._filter.filterCriteria.children = this._filter.filterCriteria.children || [];

    filters
      .filter(
        ({ filter } = {}) =>
          filter && filter.filterCriteria && (isChildren(filter.filterCriteria) || isCriteria(filter.filterCriteria))
      )
      .forEach(({ filter } = {}, index) => {
        if (this._filter.filterCriteria.children.length) {
          this._filter.filterCriteria.children[this._filter.filterCriteria.children.length - 1].union = union;
        }

        this._filter.filterCriteria.children.push(copyFilterCriteria(filter.filterCriteria));
      });
    filters.forEach(({ filter } = {}) => {
      if (filter.from || filter.to) {
        this._filter.from = filter.from;
        this._filter.to = filter.to;
      }
    });
    return this;

    function isChildren({ children } = {}) {
      return Array.isArray(children) && children.length;
    }

    function isOneChildren({ children } = {}) {
      return Array.isArray(children) && children.length === 1;
    }

    function isCriteria({ criteria } = {}) {
      return criteria;
    }

    function copyFilterCriteria(filterCriteria) {
      if (!filterCriteria) {
        return null;
      }
      let { children = [], criteria = null, union = null } = filterCriteria;
      if (isOneChildren(filterCriteria) && !isCriteria(filterCriteria)) {
        // flat de {children: [1], criteria: null} to {children: [], criteria: {1}}
        union = children[0].union || union || null;
        criteria = children[0].criteria || null;
        children = children[0].children || []; // ojo el orden importa: este debe ser ultimo
      }

      return {
        children: isChildren({ children }) ? children.map(copyFilterCriteria).filter(val => val !== null) : [],
        criteria: criteria ? criteria : null,
        union: UNIONS.includes(union) ? union.toUpperCase() : null,
      };
    }
  }

  /*
   * @property (readonly)
   * Devuelve un array plano con una referencia a todos los filterCriterias, no se puede editar
   */
  get filterCriterias() {
    const array = [];
    this.flatmap(filterCriteria => array.push(filterCriteria));
    return array;
  }

  /*
   * @property (readonly)
   * Devuelve un array plano con una referencia a todos los criterias, no se puede editar
   */
  get criterias() {
    const array = [];
    this.flatmap(filterCriteria => filterCriteria.criteria && array.push(filterCriteria.criteria));
    return array;
  }

  /*
   * @method
   * Permite ejecutar un callback sobre cada filterCriteria
   */
  flatmap(callback) {
    _flatmap(this._filter.filterCriteria);

    function _flatmap(filterCriteria, index = null, parent = null) {
      callback(filterCriteria, index, parent);
      filterCriteria.children && filterCriteria.children.forEach((fc, index) => _flatmap(fc, index, filterCriteria));
    }
  }

  removeCriteria(criteria) {
    // let parent = null;
    this.flatmap((filterCriteria, index, parent) => {
      const isFound =
        filterCriteria.criteria &&
        (criteria === filterCriteria.criteria ||
          (criteria.v1 === filterCriteria.criteria.v1 &&
            criteria.op === filterCriteria.criteria.op &&
            criteria.v2 === filterCriteria.criteria.v2));
      if (isFound) {
        if (!parent) {
          // si es el primer nivel
          filterCriteria.criteria = null;
        } else if (parent.children.length === 1) {
          // si es el ultimo children
          parent.children.splice(index, 1);
        } else {
          parent.children.splice(index, 1);
        }
      }
    });
    // faltaría un refresh del filter para borrar los filterCriteria vacios...
  }
}

Filter.toStringV2 = toStringV2;
Filter.toHumanizedCriteria = toHumanizedCriteria;
Filter.toHumanizedV2 = toHumanizedV2;
